import type { InfiniteData } from "@tanstack/react-query";
import dayjs from "dayjs";
import type { components } from "../../../types/voislog_api";
import type { ColorToken } from "../../../theme/colors/types";
import type {
  NotificationPage,
  ReminderHistPage,
} from "../../../stores/notificationStore";

type AlertType = components["schemas"]["AlertType"];
type EmailableAlertType = components["schemas"]["EmailableAlertType"];
type AlertResponse = components["schemas"]["AlertResponse"];
type ReminderHistResponse = components["schemas"]["ReminderHistResponse"];
type ReminderInfoResponse = components["schemas"]["ReminderInfoResponse"];

/** 一覧で絞り込める種別。並びはチップの並び */
export const NOTIFICATION_CATEGORIES = [
  "low",
  "high",
  "trend",
  "reminder",
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

type AlertCategory = Exclude<NotificationCategory, "reminder">;

// 本人の一覧に出す通知種別とその分け方。本人にメールが届く13種（EmailableAlertType）と一致させる
const ALERT_CATEGORY: Record<EmailableAlertType, AlertCategory> = {
  Mi1RESULT_LOW: "low",
  Mi1RESULT_VERY_LOW: "low",
  Mi1RESULT_CONSECUTIVE_LOW: "low",
  Mi1RESULT_CONSECUTIVE_VERY_LOW: "low",
  MENTAL_ACTIVITY_LOW_THAN_USUAL: "low",
  Mi1RESULT_HIGH: "high",
  Mi1RESULT_VERY_HIGH: "high",
  Mi1RESULT_CONSECUTIVE_HIGH: "high",
  Mi1RESULT_CONSECUTIVE_VERY_HIGH: "high",
  Mi1_TREND_RECENT_AVERAGE_DECLINE: "trend",
  Mi1_TREND_CONSECUTIVE_DECLINE: "trend",
  Mi1_TREND_TWO_WEEK_AVERAGE_DECLINE: "trend",
  Mi1_TREND_FREQUENCY_DECLINE: "trend",
};

// 閲覧者だけに届く種別などは出さない。APIの種別絞り込み（VOISLOG-421）が入るまではここで除外する
export function isShownAlertType(type: AlertType): type is EmailableAlertType {
  return type in ALERT_CATEGORY;
}

// 日次バッチで発生し、時刻に意味がない種別は日付だけ出す
const DATE_ONLY_ALERT_TYPES: ReadonlySet<EmailableAlertType> = new Set([
  "Mi1_TREND_TWO_WEEK_AVERAGE_DECLINE",
  "Mi1_TREND_FREQUENCY_DECLINE",
]);

/** 一覧の1行。通知履歴とリマインダー履歴を同じ形で扱う */
export type NotificationItem =
  | {
      kind: "alert";
      key: string;
      at: string;
      alertType: EmailableAlertType;
      alertInfo: AlertResponse["alert_info_json"];
    }
  | {
      kind: "reminder";
      key: string;
      at: string;
      reminderInfo: ReminderInfoResponse | null;
    };

export function categoryOf(item: NotificationItem): NotificationCategory {
  return item.kind === "reminder" ? "reminder" : ALERT_CATEGORY[item.alertType];
}

function isDateOnly(item: NotificationItem): boolean {
  return item.kind === "reminder" || DATE_ONLY_ALERT_TYPES.has(item.alertType);
}

/** 行の日時。今年は MM/DD HH:mm（日付だけの種別は MM/DD）、去年以前は YYYY/MM/DD */
export function formatNotifiedAt(item: NotificationItem, now: Date): string {
  const at = dayjs(item.at);
  if (at.year() !== dayjs(now).year()) return at.format("YYYY/MM/DD");
  return at.format(isDateOnly(item) ? "MM/DD" : "MM/DD HH:mm");
}

/** 開いた行に出す測定値の1組。mentalActivity は API のまま 0〜1 で持つ */
export type Measurement =
  | {
      kind:
        | "mindIndex"
        | "mentalActivity"
        | "recentCount"
        | "priorCount"
        | "daysSinceLastAnalysis";
      value: number;
    }
  | { kind: "lastAnalyzedAt"; at: string };

// 通知の値のキーと、出す組。並びは開いた行での並び
const ALERT_MEASUREMENT_KEYS = [
  ["sten_score", "mindIndex"],
  ["mental_activity", "mentalActivity"],
  ["recent_count", "recentCount"],
  ["prior_count", "priorCount"],
] as const;

/**
 * 開いた行に出す測定値。空なら開けない行にする。
 * 種別ごとの表は持たず、届いた値にあるものだけを出す。即時の通知はこころ指数と心の活量値
 * （こころ指数の契約が無い会社では sten_score が null なので心の活量値だけ）、
 * 測定回数の減少は2つの期間の回数、2週間の低下傾向は値が無い
 */
export function measurementsOf(item: NotificationItem): Measurement[] {
  if (item.kind === "alert") {
    return ALERT_MEASUREMENT_KEYS.flatMap<Measurement>(([key, kind]) => {
      const value = item.alertInfo[key];
      return typeof value === "number" ? [{ kind, value }] : [];
    });
  }
  const info = item.reminderInfo;
  if (info === null) return [];
  const measurements: Measurement[] = [];
  if (info.last_analyzed_at) {
    measurements.push({ kind: "lastAnalyzedAt", at: info.last_analyzed_at });
  }
  measurements.push({
    kind: "daysSinceLastAnalysis",
    value: info.days_since_last_analysis,
  });
  return measurements;
}

/** 番兵。この側の次ページの行は、一覧のこれより下に入る */
type Sentinel = { kind: "sentinel"; source: "alert" | "reminder"; at: string };

/** どの行よりも新しい時刻。番兵をここに置くと、一覧の一番上に来る */
const ABOVE_ALL_ROWS_AT = "9999-12-31T23:59:59Z";

/** 一覧に並べる履歴の、取得済みのページと続きの有無。続きの有無はクエリの hasNextPage を渡す */
type History<TPage> = { data: InfiniteData<TPage>; hasNextPage: boolean };

// 通知履歴とリマインダー履歴から、一覧に出す行を新しい順に並べる。
// 次ページがある側は番兵を入れ、最初の番兵より後ろは次ページを読むまで出さない
export function toNotificationList(
  alerts: History<NotificationPage>,
  reminders: History<ReminderHistPage>,
  enabled: Record<NotificationCategory, boolean>,
): {
  /** 表示する行。絞り込みでオフの種別は除く */
  rows: NotificationItem[];
  /** 続きを読む側。最初の番兵の側で、番兵が無ければ null */
  next: "alert" | "reminder" | null;
  /** 一覧に出す行が取得済みに1件でもあるか。絞り込みには関係しない */
  hasAny: boolean;
} {
  const items: (NotificationItem | Sentinel)[] = [];
  // ページを取る合間に新着が増えると前のページの末尾が次のページにも入るので、同じ通知は1行にする
  const seenAlertIds = new Set<AlertResponse["id"]>();
  for (const alert of alerts.data.pages.flatMap((page) => page.items)) {
    if (!isShownAlertType(alert.alert_type) || seenAlertIds.has(alert.id)) {
      continue;
    }
    seenAlertIds.add(alert.id);
    items.push({
      kind: "alert",
      key: `alert-${alert.id}`,
      at: alert.alert_at,
      alertType: alert.alert_type,
      alertInfo: alert.alert_info_json,
    });
  }
  // 番兵は取得済みの通知のうち一番古い時刻に置く。API の並び順は仕様に書かれておらず、
  // 最後の行が一番古いとは限らないので、全行から探して、取得済みの行を番兵より下に隠さない。
  // 通知は一覧に出さない種別も含めて見る。
  // まだ1件も取得していなければ、続きにはどの行より新しい通知もあり得るので、一番上に置く。
  // 絞り込みでその側を1種別も出していなければ置かない
  const oldestAlert = alerts.data.pages
    .flatMap((page) => page.items)
    .reduce<AlertResponse | undefined>(
      (oldest, alert) =>
        !oldest || new Date(alert.alert_at) < new Date(oldest.alert_at)
          ? alert
          : oldest,
      undefined,
    );
  const showsAlerts = NOTIFICATION_CATEGORIES.some(
    (category) => category !== "reminder" && enabled[category],
  );
  if (alerts.hasNextPage && showsAlerts) {
    items.push({
      kind: "sentinel",
      source: "alert",
      at: oldestAlert?.alert_at ?? ABOVE_ALL_ROWS_AT,
    });
  }
  // リマインダーも通知と同じく、ページの境目で2回来たものは1行にする
  const seenReminderIds = new Set<ReminderHistResponse["id"]>();
  for (const reminder of reminders.data.pages.flatMap((page) => page.items)) {
    if (seenReminderIds.has(reminder.id)) {
      continue;
    }
    seenReminderIds.add(reminder.id);
    items.push({
      kind: "reminder",
      key: `reminder-${reminder.id}`,
      at: reminder.sent_at,
      reminderInfo: reminder.reminder_info ?? null,
    });
  }
  // 番兵の置き方は通知と同じ。取得済みのリマインダーのうち一番古い時刻に置き、
  // まだ1件も取得していなければ一番上に置く
  const oldestReminder = reminders.data.pages
    .flatMap((page) => page.items)
    .reduce<ReminderHistResponse | undefined>(
      (oldest, reminder) =>
        !oldest || new Date(reminder.sent_at) < new Date(oldest.sent_at)
          ? reminder
          : oldest,
      undefined,
    );
  if (reminders.hasNextPage && enabled.reminder) {
    items.push({
      kind: "sentinel",
      source: "reminder",
      at: oldestReminder?.sent_at ?? ABOVE_ALL_ROWS_AT,
    });
  }
  // 同じ時刻なら入れた順（通知 → 通知の番兵 → リマインダー → リマインダーの番兵）に並ぶ
  items.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());

  const sentinel = items.find(
    (item): item is Sentinel => item.kind === "sentinel",
  );
  const shown = sentinel ? items.slice(0, items.indexOf(sentinel)) : items;
  return {
    rows: shown
      .filter((item): item is NotificationItem => item.kind !== "sentinel")
      .filter((item) => enabled[categoryOf(item)]),
    next: sentinel?.source ?? null,
    hasAny: items.some((item) => item.kind !== "sentinel"),
  };
}

/** 種別の丸とチップの色。チップの縁は文字色と同じにしてテーマ色に紛れないようにする */
export const CATEGORY_COLORS: Record<
  NotificationCategory,
  { bg: ColorToken; text: ColorToken }
> = {
  low: { bg: "notification.low_bg", text: "notification.low_text" },
  high: { bg: "notification.high_bg", text: "notification.high_text" },
  trend: { bg: "notification.trend_bg", text: "notification.trend_text" },
  reminder: {
    bg: "notification.reminder_bg",
    text: "notification.reminder_text",
  },
};
